import { useMemo, useRef, memo, useEffect } from "react";
import { useScroll, motion, useTransform, useMotionValueEvent } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { useInView } from "react-intersection-observer";
import { SentencesOnScroll } from "../../components/sentences-scroll";
import { useSectionVisibility, useScrollContext } from "../../utils/ScrollContext";

const SentencesOnScrollMemoized = memo(SentencesOnScroll);

const StudioCreation = ({ textes, setIsBackgroundWhite }) => {
  const { ref: sectionRef } = useSectionVisibility("studio-creation", 0.2);
  const { getVisibleSections } = useScrollContext();


  const [ref, inView] = useInView({
    threshold: 0, // Change this value to adjust the threshold for when the component is considered in view
    triggerOnce: false, // Only trigger the animation once
  });

  const targetRef = useRef(null);

    const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end end"],
  });


  const titleVariants = {
    hidden: { y: -50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { delay: 0, duration: 0.4, ease: "easeOut" },
    },
  };

  const descriptionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { delay: 0, duration: 0.4, ease: "easeOut" },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.4, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

    useMotionValueEvent(scrollYProgress, "change", (value) => {
    if (value > 0.45) {
      setBgWhite(false);
    } else {
      setBgWhite(true);
    }
  });

  const setBgWhite = (value) => {
    const visibleSections = getVisibleSections();
    if (visibleSections.includes("studio-creation")) {
      setIsBackgroundWhite(value);
    } 
  };


  return (
    <div ref={(ref) => {
      sectionRef(ref);
      targetRef.current = ref;
    }}
    aria-label="À propos du Studio SAM"
    >
      <motion.div
        className="grid grid-cols-2 mt-44 mx-auto max-w-[1440px]"
        ref={ref}
        variants={containerVariants}
        animate={inView ? "visible" : "hidden"}

      >
        <motion.p
          className="flex"
          style={{ fontFamily: "Lexend" }}
          variants={titleVariants}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.5, duration: 0.5, ease: "easeIn" }}
        >
          <ReactMarkdown
            className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl leading-none mb-4 text-white ml-2 sm:ml-4 lg:ml-10"
            escapeHtml={false}
            components={{
              strong: ({ node, ...props }) => (
                <span className="text-[#00ffcc] font-sam font-[200]" {...props} />
              ),
              em: ({ node, ...props }) => (
                <span className="text-[#00ffcc] font-sam font-[200] [text-stroke:1px_#00FFCC] text-transparent" {...props} />
              ),
            }}
          >
            {textes?.studioCreationTitle}
          </ReactMarkdown>
        </motion.p>

        <motion.div
          className="flex"
          style={{ fontFamily: "Lexend" }}
          variants={descriptionVariants}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0, duration: 0.5, ease: "easeIn" }}
        >
          <ReactMarkdown
            className="text-lg sm:text-xl md:text-2xl lg:text-3xl leading-relaxed text-white"
            escapeHtml={false}
            style={{ fontFamily: "Lexend" }}
            components={{
              strong: ({ node, ...props }) => (
                <span
                  className="text-strong text-sm sm:text-base md:text-lg lg:text-[15pt]"
                  {...props}
                />
              ),
            }}
          >
            {textes?.studioCreationLongDescription}
          </ReactMarkdown>
        </motion.div>
      </motion.div>
      <div className="relative h-[20vh]" />
      <div >
        <div className="h-full">
          <div className="sticky flex origin-center justify-center">
            {/* <motion.div
              style={{
                opacity: paragraph1Opacity,
                "--y": paragraph1TranslateY,
                position,
                whiteSpace: "pre-wrap",
              }}
              className="centered-text top-1/2 flex origin-center -translate-y-1/2 translate-y-centered-offset w-[300px] pr-16 flex items-center justify-center h-screen w-screen leading-tight text-white text-center text-sm md:text-sm lg:text-sm"
            >
              {textes?.intro1 && (
                <SentencesOnScroll
                  sentences={textes?.intro1.split("\n")}
                  isVisible={true}
                />
              )}
            </motion.div> */}
            <motion.div
              style={{
                whiteSpace: "pre-wrap",
              }}
              className="centered-text flex origin-center w-[300px] pr-16 items-center justify-center h-full w-full text-sm md:text-sm lg:text-sm leading-tight text-white text-center"
            >
              {textes?.intro2 && (
                <SentencesOnScrollMemoized
                  sentences={textes?.intro2.split("\n")}
                  isVisible={true}
                  noAnims={true}
                />
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudioCreation;
